import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  cros3Forms: [],
  currentCros3Form: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all CROS-3 Forms
export const fetchCros3Forms = createAsyncThunk(
  'cros3Forms/fetchCros3Forms',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/cros3-forms/${patientId}` : '/cros3-forms';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch CROS-3 forms'
      );
    }
  }
);

// Fetch a single CROS-3 Form
export const fetchCros3Form = createAsyncThunk(
  'cros3Forms/fetchCros3Form',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/cros3-forms/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch CROS-3 form'
      );
    }
  }
);

// Create a new CROS-3 Form
export const createCros3Form = createAsyncThunk(
  'cros3Forms/createCros3Form',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/cros3-forms', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create CROS-3 form'
      );
    }
  }
);

// Update an existing CROS-3 Form
export const updateCros3Form = createAsyncThunk(
  'cros3Forms/updateCros3Form',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/cros3-forms/${id}`, formData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update CROS-3 form'
      );
    }
  }
);

// Delete a CROS-3 Form
export const deleteCros3Form = createAsyncThunk(
  'cros3Forms/deleteCros3Form',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/cros3-forms/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete CROS-3 form'
      );
    }
  }
);

// ==================== Slice ====================
const cros3FormSlice = createSlice({
  name: 'cros3Forms',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentCros3Form: (state, action) => {
      state.currentCros3Form = action.payload;
    },
    clearCurrentCros3Form: (state) => {
      state.currentCros3Form = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchCros3Forms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCros3Forms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cros3Forms = action.payload;
      })
      .addCase(fetchCros3Forms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchCros3Form.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCros3Form.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCros3Form = action.payload;
      })
      .addCase(fetchCros3Form.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createCros3Form.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCros3Form.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cros3Forms.unshift(action.payload);
      })
      .addCase(createCros3Form.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateCros3Form.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCros3Form.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;
        const index = state.cros3Forms.findIndex((d) => d.id === updated.id);
        if (index !== -1) {
          state.cros3Forms[index] = updated;
        }
        if (state.currentCros3Form?.id === updated.id) {
          state.currentCros3Form = updated;
        }
      })
      .addCase(updateCros3Form.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteCros3Form.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCros3Form.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cros3Forms = state.cros3Forms.filter(
          (d) => d.id !== action.payload
        );
        if (state.currentCros3Form?.id === action.payload) {
          state.currentCros3Form = null;
        }
      })
      .addCase(deleteCros3Form.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentCros3Form,
  clearCurrentCros3Form,
} = cros3FormSlice.actions;

export default cros3FormSlice.reducer;
